import { Q_AND_A_QUESTION_STATUS } from './components/QAndARoleFilter/data';
import { Q_AND_A_ROLES, QAndARoles, QuestionStatus } from './models/QAndA';
import {
  DATA_ROOM_MEMBER_ROLES,
  DataRoomMemberRole,
} from './models/DataRoomMember';
import {
  DataRoomShareableLinkAccess,
  DATA_ROOM_SHAREABLE_LINK_ACCESS,
  DataRoomShareableLinkExpires,
  DATA_ROOM_SHAREABLE_LINK_EXPIRES,
} from 'models/ShareableLink';
import { getAvailabilityState } from './components/DataRoomDocumentsRouter/components/DataRoomDocumentDrawers/ManageFileAccess/utils';
import { DataRoomSecuritySettings } from './models/DataRoom';
import { BulkFileSettings } from './models/DataRoomBulk';
import {
  ADDITIONAL_DOCUMENTS,
  CATEGORY_SORTING_VALUES,
  DATA_ROOM_RFI_FILE_STATUS,
  DATA_ROOM_RFI_STATUS,
  DataRoomRFIFileStatus,
  DataRoomRFIStatus,
  DataRoomRfiTemplate,
  FileTypeRFI,
} from './models/DataRoomRfi';
import {
  getIsDesc,
  getSortBy,
  getSortComparator,
  getUserFullName,
} from 'utils';

export const isDataRoomOwner = (role: DataRoomMemberRole) =>
  role === DATA_ROOM_MEMBER_ROLES.OWNER;

export const isDataRoomAdmin = (role: DataRoomMemberRole) =>
  role === DATA_ROOM_MEMBER_ROLES.ADMIN;

export const isDataRoomPublisher = (role: DataRoomMemberRole) =>
  role === DATA_ROOM_MEMBER_ROLES.PUBLISHER;

export const isDataRoomViewerDownloader = (role: DataRoomMemberRole) =>
  role === DATA_ROOM_MEMBER_ROLES.VIEWER_DOWNLOADER;

export const isDataRoomViewer = (role: DataRoomMemberRole) =>
  role === DATA_ROOM_MEMBER_ROLES.VIEWER;

export const isDataRoomAdminOrOwner = (role: DataRoomMemberRole) =>
  isDataRoomAdmin(role) || isDataRoomOwner(role);

// QA

export const isQARoleNone = (qaRole: QAndARoles) =>
  qaRole === Q_AND_A_ROLES.NONE;

// Sell side

export const isQASellOwner = (qaRole: QAndARoles) =>
  qaRole === Q_AND_A_ROLES.OWNER;

export const isQASellAdmin = (qaRole: QAndARoles) =>
  qaRole === Q_AND_A_ROLES.ADMIN_SELL;

export const isQASellCoordinator = (qaRole: QAndARoles) =>
  qaRole === Q_AND_A_ROLES.COORDINATOR;

export const isQASellExpert = (qaRole: QAndARoles) =>
  qaRole === Q_AND_A_ROLES.EXPERT;

export const isQASellAnswerApprover = (qaRole: QAndARoles) =>
  qaRole === Q_AND_A_ROLES.ANSWER_APPROVER;

// Buy side

export const isQABuyAdmin = (qaRole: QAndARoles) =>
  qaRole === Q_AND_A_ROLES.ADMIN_BUY;

export const isQABuyQuestionApprover = (qaRole: QAndARoles) =>
  qaRole === Q_AND_A_ROLES.QUESTION_APPROVER;

export const isQABuyDrafter = (qaRole: QAndARoles) =>
  qaRole === Q_AND_A_ROLES.DRAFTER;

export const isUserQaAdmin = (qaRole: QAndARoles) =>
  isQASellAdmin(qaRole) || isQASellOwner(qaRole);

export const hasUserFaqPermission = (qaRole: QAndARoles) =>
  isUserQaAdmin(qaRole) || isQASellAnswerApprover(qaRole);

export const canUserManageSubject = (qaRole: QAndARoles) =>
  isUserQaAdmin(qaRole) || isQASellCoordinator(qaRole);

export const isBuyManageRole = (qaRole: QAndARoles) =>
  isQABuyAdmin(qaRole) || isQABuyQuestionApprover(qaRole);

// Questions

export const isQuestionDrafted = (status: QuestionStatus) =>
  status === Q_AND_A_QUESTION_STATUS.DRAFTED;

export const isQuestionSellAsigned = (status: QuestionStatus) =>
  status === Q_AND_A_QUESTION_STATUS.SELL_SIDE_ASIGNED;

export const isQuestionAnswerRejected = (status: QuestionStatus) =>
  status === Q_AND_A_QUESTION_STATUS.ANSWER_REJECTED;

export const isQuestionRejected = (status: QuestionStatus) =>
  status === Q_AND_A_QUESTION_STATUS.QUESTION_REJECTED;

export const isQuestionAnswered = (status: QuestionStatus) =>
  status === Q_AND_A_QUESTION_STATUS.ANSWERED;

export const isQuestionAnswerApproved = (status: QuestionStatus) =>
  status === Q_AND_A_QUESTION_STATUS.ANSWER_APPROVED;

export const isQuestionApproved = (status: QuestionStatus) =>
  status === Q_AND_A_QUESTION_STATUS.QUESTION_APPROVED;

export const getQuestionSubject = (subject: string, otherSubject: string) =>
  subject === 'Other' ? `Other: ${otherSubject}` : subject;

// Shareable links

export const getIsAccessAnyone = (linkAccess: DataRoomShareableLinkAccess) =>
  linkAccess === DATA_ROOM_SHAREABLE_LINK_ACCESS.ANYONE_WITH_LINK;

export const getIsAccessAddPasword = (
  linkAccess: DataRoomShareableLinkAccess,
) => linkAccess === DATA_ROOM_SHAREABLE_LINK_ACCESS.ADD_PASSWORD;

export const getIsAccessAddSpecificRecipiens = (
  linkAccess: DataRoomShareableLinkAccess,
) => linkAccess === DATA_ROOM_SHAREABLE_LINK_ACCESS.SPECIFIC_RECIPIENTS;

export const getIsLinkExpiresOther = (
  linkExpires: DataRoomShareableLinkExpires,
) => linkExpires === DATA_ROOM_SHAREABLE_LINK_EXPIRES.OTHER;

// file settings

export const getInitialStateForFileSettings = (checked: boolean) => ({
  checked,
  isMixed: false,
  state: getAvailabilityState(checked),
});

export const getPayloadBulkFileSettings = ({
  downloadSecurePdf,
  hasExpiration,
  expirationDays,
  includeWatermark,
  watermarkDiagonal,
  watermarkHeader,
  watermarkFooter,
  watermarkFullName,
  watermarkEmail,
  watermarkIpAddress,
}: BulkFileSettings): DataRoomSecuritySettings => {
  return {
    downloadSecurePdf: !!downloadSecurePdf.checked,
    hasExpiration: !!hasExpiration.checked,
    expirationDays: +expirationDays,
    includeWatermark: !!includeWatermark.checked,
    watermarkDiagonal: !!watermarkDiagonal.checked,
    watermarkHeader: !!watermarkHeader.checked,
    watermarkFooter: !!watermarkFooter.checked,
    watermarkFullName: !!watermarkFullName.checked,
    watermarkEmail: !!watermarkEmail.checked,
    watermarkIpAddress: !!watermarkIpAddress.checked,
  };
};

// RFI

export const getIsRfiFileStatusUploaded = (status: DataRoomRFIFileStatus) =>
  status === DATA_ROOM_RFI_FILE_STATUS.UPLOADED;

export const getIsRfiFileStatusAdded = (status: DataRoomRFIFileStatus) =>
  status === DATA_ROOM_RFI_FILE_STATUS.ADDED;

export const getIsRfiFileStatusIgnored = (status: DataRoomRFIFileStatus) =>
  status === DATA_ROOM_RFI_FILE_STATUS.IGNORED;

export const getIsRfiStatusSubmitted = (status: DataRoomRFIStatus) =>
  status === DATA_ROOM_RFI_STATUS.SUBMITTED;

export const getIsRfiStatusClosed = (status: DataRoomRFIStatus) =>
  status === DATA_ROOM_RFI_STATUS.CLOSED;

export const getIsRfiStatusPending = (status: DataRoomRFIStatus) =>
  status === DATA_ROOM_RFI_STATUS.PENDING;

export const getIsAdditionalDocuments = (name: string) =>
  name === ADDITIONAL_DOCUMENTS;

export const sortRfiFiles = (
  files: FileTypeRFI[],
  sort: string,
): FileTypeRFI[] => {
  if (!sort) return files;

  const isDesc = getIsDesc(sort);
  const sortBy = getSortBy(sort);

  return [...files].sort((a, b) => {
    const isUploaderSort = sortBy === CATEGORY_SORTING_VALUES.UPLOADER;

    const aVal = isUploaderSort
      ? getUserFullName(
          a.dataRoomRFIFileUploader?.firstName,
          a.dataRoomRFIFileUploader?.lastName,
        )
      : a.file?.name;

    const bVal = isUploaderSort
      ? getUserFullName(
          b.dataRoomRFIFileUploader?.firstName,
          b.dataRoomRFIFileUploader?.lastName,
        )
      : b.file?.name;

    return getSortComparator(aVal, bVal, isDesc);
  });
};

export const sortTemplates = (templates: DataRoomRfiTemplate[]) =>
  templates.sort((first, second) => {
    const firstDate = new Date(first.createdAt).getTime();
    const secondDate = new Date(second.createdAt).getTime();

    return firstDate > secondDate ? -1 : 1;
  });
